import React, { useCallback, useEffect, useState, useRef } from "react";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import TipTapToolbar from "./TipTapToolbar";
import VariableHighlight from "./VariableHighlightExtension";
import EmailSafeImage from "./EmailSafeImageExtension";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { getInvalidVariables } from "@/utils/templateUtils";
import { EmailSafeExtensions, CleanHtml } from "./EmailSafeExtensions";

import './editor-styles.css';

interface TemplateEditorProps {
  htmlContent: string;
  onHtmlChange: (html: string) => void;
  editorRef?: React.MutableRefObject<{ insertVariable?: (variable: string) => void } | null>;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({
  htmlContent,
  onHtmlChange,
  editorRef,
}) => {
  const [invalidVariables, setInvalidVariables] = useState<string[]>([]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      const htmlInvalidVars = getInvalidVariables(htmlContent);
      setInvalidVariables(htmlInvalidVars);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [htmlContent]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      VariableHighlight,
      EmailSafeImage,
      // Add our email-safe extensions
      ...EmailSafeExtensions,
      CleanHtml,
    ],
    content: htmlContent || ' ', // Initialize with space to prevent placeholder overlap
    onUpdate: ({ editor }) => {
      onHtmlChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor-content',
      },
    },
  });

  // Sync editor content with htmlContent prop
  useEffect(() => {
    if (editor && htmlContent !== editor.getHTML()) {
      // Only update if content is actually different
      editor.commands.setContent(htmlContent || ' ');
    }
  }, [editor, htmlContent]);

  // Expose insertVariable method through ref
  useEffect(() => {
    if (!editorRef) return;

    editorRef.current = {
      insertVariable: (variable: string) => {
        try {
          if (editor) {
            editor.commands.focus();
            return editor.commands.insertContent(variable);
          }
        } catch (e) {
          console.error("Error in insertVariable:", e);
        }
        
        return false;
      }
    };
    
    return () => {
      if (editorRef) editorRef.current = null;
    };
  }, [editor, editorRef]);

  return (
    <div className="border rounded-md">
      {invalidVariables.length > 0 && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Invalid Template Variables</AlertTitle>
          <AlertDescription>
            The following variables are not recognized: 
            {invalidVariables.map((v, i) => (
              <span key={v} className="font-mono mx-1">
                {"{{" + " " + v + " " + "}}"}
                {i < invalidVariables.length - 1 ? "," : ""}
              </span>
            ))}
          </AlertDescription>
        </Alert>
      )}
      
      <div className="w-full">
        <TipTapToolbar editor={editor} />
        <div className="p-4">
          <EditorContent 
            editor={editor} 
            className="prose max-w-none min-h-[300px] p-2 border rounded-md tiptap-editor-wrapper"
          />
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;